import { useEffect, useState, useRef } from "react"
import { RootState } from "../../../App/store"
import { useSelector } from "react-redux"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import { SendIcon } from "../../Shared/Components/SvgIcons"
import HTTPService from "../../../App/HTTPService"
import { headers } from "../../../App/Axios"
import { ConversationMessage } from "../../../Types/Conversation"
import CharacterCounter from "../../Shared/Components/CharacterCounter"
import { ConversationMessagePatchRequest } from "../ChatServiceRequest"

function ChatTextbox(props: { onSendMessage: (message: ConversationMessage) => void, onMessageEdited: (message: ConversationMessage) => void, editingMessage?: ConversationMessage }) {
    const activeConversationId = useSelector((state: RootState) => state.chatSlice.activeConversationId)
    const translation = useSelector(selectTranslations)
    const nativeInputRef = useRef<HTMLTextAreaElement | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [mobileMessage, setMobileMessage] = useState("")
    const chatTextLength = mobileMessage.trim().length
    const sendDisabled = isSubmitting || chatTextLength === 0 || chatTextLength > 2500

    const clearEditor = () => {
        setMobileMessage("")
    }

    useEffect(() => {
        setMobileMessage("")
    }, [activeConversationId])

    useEffect(() => {
        if (!props.editingMessage) {
            clearEditor()
            return
        }

        const parser = new DOMParser()
        const plainText = (parser.parseFromString(props.editingMessage.content, "text/html").body.textContent || "").trim()
        setMobileMessage(plainText)
    }, [props.editingMessage])

    const getMobilePayloadContent = () => {
        const normalized = mobileMessage.trim().replace(/\r\n/g, "\n").replace(/\r/g, "\n")
        return normalized.replace(/\n/g, "<br>")
    }

    const resizeNativeInput = () => {
        if (!nativeInputRef.current) {
            return
        }

        const input = nativeInputRef.current
        input.style.height = "auto"
        const isMobileViewport = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
        const maxHeightPx = isMobileViewport ? 96 : 72
        const nextHeight = Math.min(input.scrollHeight, maxHeightPx)
        input.style.height = `${nextHeight}px`
        input.style.overflowY = input.scrollHeight > maxHeightPx ? "auto" : "hidden"
    }

    const submitMessage = async () => {
        if (sendDisabled) {
            return
        }

        setIsSubmitting(true)
        try {
            if (props.editingMessage) {
                const payloadContent = getMobilePayloadContent()

                const response = await ConversationMessagePatchRequest(
                    activeConversationId,
                    props.editingMessage.id,
                    payloadContent
                )

                if (response.status === 200) {
                    clearEditor()
                    props.onMessageEdited(response.data)
                }
            } else {
                const payloadContent = getMobilePayloadContent()

                const response = await HTTPService.post(
                    "/conversation/" + activeConversationId + "/message",
                    { content: payloadContent, conversation_id: activeConversationId },
                    headers
                )

                if (response.status === 201) {
                    clearEditor()
                    props.onSendMessage(response.data)
                }
            }
        } catch (error: any) {
            if (error.response?.status === 400) {
                //TODO Error handling
            }
        }
        setIsSubmitting(false)
    }

    useEffect(() => {
        resizeNativeInput()
    }, [mobileMessage])

    return (
        <div className="chat-layout__right-panel-input-container">
            <div className="chat-layout__photo-text-container">
                <div className="chat-layout__native-composer">
                    <textarea
                        ref={nativeInputRef}
                        className="chat-layout__native-input"
                        placeholder={translation.typeChatMessage}
                        value={mobileMessage}
                        onChange={(event) => setMobileMessage(event.target.value)}
                        onInput={resizeNativeInput}
                        rows={1}
                    />
                    <CharacterCounter max={2500} current={chatTextLength} modifier="chat" />
                </div>
            </div>
            <div className="chat-layout__right-panel-icons-container">
                {!sendDisabled ? (
                    <div className="chat-layout__right-panel-icon" onClick={submitMessage}>
                        <SendIcon color="green" />
                    </div>
                ) : (
                    <div className="chat-layout__right-panel-icon chat-layout__right-panel-icon--disable">
                        <SendIcon color="grey" />
                    </div>
                )}

            </div>
        </div>
    )
}
export default ChatTextbox
